
import { supabase } from '@/integrations/supabase/client';
import { TimetableConfig, TimetableEntry } from '../timetable/fileStorageService';
import { mediaStorage } from '../../storage/mediaStorage';

class TimetableSupabaseService {
  private syncStatus: 'synced' | 'pending' | 'offline' = 'offline';
  private lastSyncAttempt: Date | null = null;
  private syncQueue: Set<string> = new Set();

  async syncAllTimetables(): Promise<TimetableConfig[]> {
    try {
      console.log('Starting timetable sync from Supabase...');
      this.syncStatus = 'pending';

      // Fetch all timetable configurations
      const { data: configsData, error: configsError } = await supabase
        .from('timetable_configs')
        .select('*')
        .order('created_at', { ascending: false });

      if (configsError) {
        console.error('Error syncing timetable configs:', configsError);
        this.syncStatus = 'offline';
        return [];
      }

      if (!configsData || configsData.length === 0) {
        console.log('No timetable configs found in Supabase');
        this.syncStatus = 'synced';
        return [];
      }

      // Fetch entries for all configurations
      const { data: entriesData, error: entriesError } = await supabase
        .from('timetable_entries')
        .select('*')
        .order('start_time', { ascending: true });

      if (entriesError) {
        console.error('Error syncing timetable entries:', entriesError);
        this.syncStatus = 'offline';
        return [];
      }

      // Group entries by timetable config
      const entriesByConfig = new Map<string, any[]>();
      if (entriesData) {
        entriesData.forEach(entry => {
          if (!entriesByConfig.has(entry.timetable_config_id)) {
            entriesByConfig.set(entry.timetable_config_id, []);
          }
          entriesByConfig.get(entry.timetable_config_id)!.push(entry);
        });
      }

      // Transform to local structure
      const enrichedConfigs: TimetableConfig[] = configsData.map(config => ({
        id: config.id,
        name: config.name,
        description: config.description || '',
        startDate: config.start_date,
        endDate: config.end_date,
        entries: (entriesByConfig.get(config.id) || []).map(entry => ({
          id: entry.id,
          day: entry.day,
          startTime: entry.start_time,
          endTime: entry.end_time,
          subject: entry.subject,
          teacherId: entry.teacher_id,
          room: entry.room,
          studentGroup: entry.student_group,
          color: entry.color || '#3b82f6',
          createdAt: entry.created_at,
          updatedAt: entry.updated_at
        })),
        documents: Array.isArray(config.documents) 
          ? config.documents.filter((doc): doc is string => typeof doc === 'string')
          : [],
        createdAt: config.created_at,
        updatedAt: config.updated_at
      }));

      this.syncStatus = 'synced';
      this.lastSyncAttempt = new Date();
      console.log(`Successfully synced ${enrichedConfigs.length} timetable configs from Supabase`);
      
      return enrichedConfigs;
    } catch (error) {
      console.error('Failed to sync timetables from Supabase:', error);
      this.syncStatus = 'offline';
      return [];
    }
  }

  async saveTimetableConfig(config: TimetableConfig): Promise<void> {
    try {
      console.log(`Syncing timetable config ${config.id} to Supabase...`);
      this.syncQueue.add(config.id);

      // Save main config record
      const { error: configError } = await supabase
        .from('timetable_configs')
        .upsert({
          id: config.id,
          name: config.name,
          description: config.description,
          start_date: config.startDate,
          end_date: config.endDate,
          documents: config.documents || [],
          created_at: config.createdAt,
          updated_at: config.updatedAt
        });

      if (configError) {
        console.error('Error saving timetable config to Supabase:', configError);
        this.syncQueue.delete(config.id);
        return;
      }

      // Save entries
      if (config.entries && config.entries.length > 0) {
        await this.syncTimetableEntries(config.id, config.entries);
      }

      this.syncQueue.delete(config.id);
      console.log(`Successfully synced timetable config ${config.id} to Supabase`);
    } catch (error) {
      console.error('Failed to save timetable config to Supabase:', error);
      this.syncQueue.delete(config.id);
    }
  }

  private async syncTimetableEntries(configId: string, entries: TimetableEntry[]): Promise<void> {
    try {
      // Delete existing entries for this config
      await supabase
        .from('timetable_entries')
        .delete()
        .eq('timetable_config_id', configId);

      // Insert new entries
      if (entries.length > 0) {
        const { error } = await supabase
          .from('timetable_entries')
          .insert(entries.map(entry => ({
            id: entry.id,
            timetable_config_id: configId,
            day: entry.day,
            start_time: entry.startTime,
            end_time: entry.endTime,
            subject: entry.subject,
            teacher_id: entry.teacherId ? parseInt(entry.teacherId, 10) : null,
            room: entry.room,
            student_group: entry.studentGroup,
            color: entry.color || '#3b82f6',
            created_at: entry.createdAt,
            updated_at: entry.updatedAt
          })));

        if (error) {
          console.error('Error syncing timetable entries:', error);
        }
      }
    } catch (error) {
      console.error('Failed to sync timetable entries:', error);
    }
  }

  async deleteTimetableConfig(configId: string): Promise<void> {
    try {
      console.log(`Deleting timetable config ${configId} from Supabase...`);
      
      const { error } = await supabase
        .from('timetable_configs')
        .delete()
        .eq('id', configId);

      if (error) {
        console.error('Error deleting timetable config from Supabase:', error);
        return;
      }

      console.log(`Successfully deleted timetable config ${configId} from Supabase`);
    } catch (error) {
      console.error('Failed to delete timetable config from Supabase:', error);
    }
  }

  async syncClassStatus(classId: string, status: string, notes?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('class_status')
        .upsert({
          id: `status_${classId}`,
          class_id: classId,
          status: status,
          notes: notes || '',
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error syncing class status:', error);
      } else {
        console.log(`Successfully synced class status for ${classId}: ${status}`);
      }
    } catch (error) {
      console.error('Failed to sync class status:', error);
    }
  }

  async getClassStatuses(): Promise<Record<string, any>> {
    try {
      const { data, error } = await supabase
        .from('class_status')
        .select('*');

      if (error) {
        console.error('Error fetching class statuses:', error);
        return {};
      }

      const statusMap: Record<string, any> = {};
      if (data) {
        data.forEach(status => {
          statusMap[status.class_id] = {
            status: status.status,
            notes: status.notes,
            updatedAt: status.updated_at
          };
        });
      }

      return statusMap;
    } catch (error) {
      console.error('Failed to fetch class statuses:', error);
      return {};
    }
  }

  getSyncStatus(): 'synced' | 'pending' | 'offline' {
    return this.syncStatus;
  }

  getLastSyncAttempt(): Date | null {
    return this.lastSyncAttempt;
  }

  getPendingSyncIds(): string[] {
    return Array.from(this.syncQueue);
  }

  async testConnection(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('timetable_configs')
        .select('count')
        .limit(1);
      
      return !error;
    } catch (error) {
      return false;
    }
  }
}

export const timetableSupabaseService = new TimetableSupabaseService();
